import React, {useState, useEffect, useCallback} from "react";
import {inject, observer} from "mobx-react";
import {useTranslation} from "react-i18next";
//local
import {Preloader} from "components/Preloader";
import DaluxSettings from "./OrganizationSettings/DaluxSettings";
import AccountsSettings from "./OrganizationSettings/AccountsSettings";
import ContractTemplateLinkSettings from "./OrganizationSettings/ContractTemplateLinkSettings";
import SmartTemplatesSettings from "./OrganizationSettings/SmartTemplatesSettings";
import EditorTemplatesSettings from "./OrganizationSettings/EditorTemplatesSettings";
import DepartmentSettings from "./OrganizationSettings/DepartmentSettings";
import MonitorSuppliersSettings from "./OrganizationSettings/MonitorSuppliersSettings";
import CustomCheckpointsSettings from "./OrganizationSettings/CustomCheckpointsSettings";
import OurSuppliersQualificationSettings from "./OrganizationSettings/OurSuppliersQualificationSettings";
import OpenRfpsSettings from "./OrganizationSettings/OpenRfpsSettings";
import DefaultCurrencySettings from "./OrganizationSettings/DefaultCurrencySettings";
import ContractNumbersSettings from "./OrganizationSettings/ContractNumbersSettings";
import SignContractInvoicingProfilesSettings from "./OrganizationSettings/SignContractInvoicingProfilesSettings";
import {hasUserPermission} from "helpers/helper";
import {showError} from "helpers/notifications.helper";
import {roles} from "constants/auth.const";

export const OrganizationSettings = inject("store")(
  observer(({store: {organization, auth, clientStore, nsi}}) => {
    const {t} = useTranslation();
    const {clientRole, organizationName, activeOrganization} = auth;
    const {departments, markings} = organization;
    const [settings, setSettings] = useState(null);

    const isAdminOwner = hasUserPermission([roles.ADMIN, roles.OWNER], clientRole);
    const isOwner = hasUserPermission([roles.OWNER], clientRole);

    const getOrganizationDepartmentLabels = useCallback(
      () => organization.getOrganizationDepartmentLabels(),
      [organization]
    );

    const handleUpdate = (
      values,
      canUpdate = isAdminOwner,
      errorTextKey = "WRONG_PRIVILEGES_ONLY_OWNER_ADMIN"
    ) =>
      canUpdate
        ? organization.updateOrganizationSettings(values).then((data) => {
            setSettings(data);
          })
        : showError(errorTextKey);

    useEffect(() => {
      organization.getOrganizationSettings().then(setSettings);
      getOrganizationDepartmentLabels();
    }, [organization, getOrganizationDepartmentLabels]);

    return settings ? (
      <div className="organization-settings__wrapper">
        <h3 className="title">{t("ORGANISATION_SETTINGS_TITLE")}</h3>
        <p className="info">{t("ORGANISATION_SETTINGS_INFO_TEXT")}</p>
        <div className="organization-settings__columns">
          <div className="organization-settings__left-column">
            <ContractNumbersSettings settings={settings} onUpdate={handleUpdate} />
            <DefaultCurrencySettings
              settings={settings}
              getCurrencies={nsi.getCurrencies}
              onUpdate={handleUpdate}
            />
            <OpenRfpsSettings settings={settings} onUpdate={handleUpdate} />
            <OurSuppliersQualificationSettings
              settings={settings}
              organizationName={organizationName}
              getQualificationQuestions={clientStore.getQualificationQuestions}
              isOwner={isOwner}
              onUpdate={handleUpdate}
            />
            <MonitorSuppliersSettings
              settings={settings}
              organization={activeOrganization}
              isAdminOwner={isAdminOwner}
              onUpdate={handleUpdate}
            />
            <CustomCheckpointsSettings
              settings={settings}
              markings={markings}
              isAdminOwner={isAdminOwner}
              getMarkings={() => organization.getOrganizationMarkings()}
              onCreate={organization.createOrganizationMarking}
              onUpdate={organization.updateOrganizationMarking}
              onDelete={organization.deleteOrganizationMarking}
            />
          </div>

          <div className="organization-settings__right-column">
            <DepartmentSettings
              settings={settings}
              departments={departments}
              isOwner={isOwner}
              getLabels={getOrganizationDepartmentLabels}
              createLabel={organization.createOrganizationDepartmentLabel}
              updateLabel={organization.updateOrganizationDepartmentLabel}
              removeLabel={organization.deleteOrganizationDepartmentLabel}
              onUpdate={handleUpdate}
            />
            <ContractTemplateLinkSettings settings={settings} onUpdate={handleUpdate} />
            <SmartTemplatesSettings isAdminOwner={isAdminOwner} />
            <EditorTemplatesSettings isAdminOwner={isAdminOwner} />
            <AccountsSettings settings={settings} onUpdate={handleUpdate} />
            <DaluxSettings
              settings={settings}
              organization={activeOrganization}
              isOwner={isOwner}
              isAdminOwner={isAdminOwner}
              onUpdate={handleUpdate}
            />
            <SignContractInvoicingProfilesSettings isOwner={isOwner} />
          </div>
        </div>
      </div>
    ) : (
      <Preloader addClass="preloader250" />
    );
  })
);
